import PageShell from '@/pages/shared/PageShell';

export default function CompareGanttTools() {
  return (
    <PageShell
      title="ガントチャートツール比較｜無料・有料・AI対応ツールを徹底比較｜”おもいむすび”"
      description="ガントチャート作成ツールの比較。Excel、無料ツール、有料ツール、AI対応ツールまで。依存関係、マイルストーン、予実比較、自動更新機能などを項目別に比較します。"
      keywords="ガントチャート ツール 比較,ガントチャート アプリ 比較,AI ガントチャート 比較,ガントチャート 作成ツール おすすめ"
      canonical="https://omoimusubi.asia/compare/gantt-chart-tools/"
      h1="ガントチャートツールの比較と選び方"
      subtitle="ガントチャートを作成・管理するためのツールは多数あります。Excelから専用ツール、AI対応ツールまで、目的別に比較します。"
      relatedLinks={[
        { label: 'AIガントチャート', path: '/features/ai-gantt/' },
        { label: 'ガントチャートとは？作り方とツールの選び方', path: '/guides/gantt-chart/' },
        { label: 'ガントチャートExcelテンプレート', path: '/templates/gantt-chart-excel/' },
        { label: 'プロジェクト管理ツール比較', path: '/compare/project-management-tools/' },
      ]}
    >
      <div className="not-prose mb-8 rounded-xl overflow-hidden border border-background-200/70">
        <img
          src="https://readdy.ai/api/search-image?query=A%20bright%20modern%20Japanese%20office%20scene%20a%20Japanese%20project%20manager%20a%20woman%20in%20her%2030s%20standing%20in%20front%20of%20a%20large%20whiteboard%20wall%20showing%20a%20hand%20drawn%20gantt%20chart%20with%20colorful%20sticky%20notes%20and%20timeline%20markers%20she%20is%20pointing%20at%20the%20chart%20with%20a%20marker%20pen%20natural%20daylight%20flooding%20the%20room%20through%20floor%20to%20ceiling%20windows%20green%20plants%20modern%20minimalist%20Japanese%20workplace%20refreshing%20creative%20atmosphere&width=1200&height=600&seq=compare-gantt-tools-01&orientation=landscape"
          alt="ガントチャートツール比較 - ガントチャートを描きながらツールを検討するプロジェクトマネージャー"
          className="w-full h-auto"
        />
      </div>
      <section>
        <h2>ガントチャートツールの種類</h2>
        <p>ガントチャートを作成するツールは、大きく以下の4タイプに分類されます。プロジェクトの規模や目的に応じて、最適なタイプを選びましょう。</p>
        <ul>
          <li><strong>Excel・スプレッドシート</strong>：テンプレートを使った手動作成。小規模・短期間のプロジェクト向き。</li>
          <li><strong>無料ガントチャートツール</strong>：基本的なガントチャート機能を無料で利用可能。個人または小規模チーム向き。</li>
          <li><strong>有料プロジェクト管理ツール</strong>：ガントチャートを含む総合的なプロジェクト管理機能。中〜大規模向き。</li>
          <li><strong>AI対応ガントチャートツール</strong>：AIによる自動生成・自動更新機能を搭載。効率化を重視するチーム向き。</li>
        </ul>
      </section>

      <div className="not-prose my-8 overflow-x-auto">
        <h3 className="text-base font-bold text-foreground-900 mb-4">ガントチャートツールのタイプ別比較</h3>
        <table className="min-w-full border-collapse text-xs">
          <thead>
            <tr className="border-b border-background-300">
              <th className="text-left p-3 font-bold text-foreground-900 bg-background-100 sticky left-0 min-w-[180px]">機能</th>
              <th className="text-center p-3 font-bold text-foreground-900 bg-background-100 min-w-[120px]">Excel</th>
              <th className="text-center p-3 font-bold text-foreground-900 bg-background-100 min-w-[120px]">無料ツール</th>
              <th className="text-center p-3 font-bold text-foreground-900 bg-background-100 min-w-[120px]">有料ツール</th>
              <th className="text-center p-3 font-bold text-primary-700 bg-primary-50 min-w-[140px]">”おもいむすび”<br/>(AI対応)</th>
            </tr>
          </thead>
          <tbody className="text-foreground-600">
            {[
              { label: 'ガントチャート作成', excel: '△（手動作成）', free: '○（手動作成）', paid: '◎（手動作成＋テンプレート）', tm: '◎（AIが自動生成）' },
              { label: '依存関係の矢印', excel: '×（手動描画）', free: '△（一部対応）', paid: '◎', tm: '◎（AIが自動設定）' },
              { label: 'マイルストーン', excel: '△（セルの色分け等）', free: '○', paid: '◎', tm: '◎（AIが自動検出）' },
              { label: '予実比較', excel: '△（別シートで管理）', free: '×', paid: '○', tm: '◎（自動比較表示）' },
              { label: 'ベースライン比較', excel: '×', free: '×', paid: '△（一部対応）', tm: '◎' },
              { label: '遅延タスクの自動表示', excel: '×', free: '×', paid: '△', tm: '◎（色分け強調表示）' },
              { label: 'ドラッグ＆ドロップ', excel: '×', free: '○', paid: '◎', tm: '◎' },
              { label: '担当者別表示', excel: '△（フィルタ）', free: '△', paid: '◎', tm: '◎' },
              { label: '進捗の自動更新', excel: '×', free: '×', paid: '×', tm: '◎（日報からAIが自動更新）' },
              { label: '多言語対応', excel: '△（言語版依存）', free: '×', paid: '△', tm: '◎（日英タイ）' },
              { label: '国別祝日反映', excel: '×', free: '×', paid: '×', tm: '◎（メンバー別に自動反映）' },
              { label: '今日のタスク自動抽出', excel: '×', free: '×', paid: '△', tm: '◎' },
            ].map((row) => (
              <tr key={row.label} className="border-b border-background-200/70 hover:bg-background-100/50">
                <td className="p-3 font-medium text-foreground-800 bg-background-100/50 sticky left-0">{row.label}</td>
                <td className="p-3 text-center">{row.excel}</td>
                <td className="p-3 text-center">{row.free}</td>
                <td className="p-3 text-center">{row.paid}</td>
                <td className="p-3 text-center font-medium text-primary-700">{row.tm}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-foreground-500 mt-3">※ 各ツールの機能はバージョンにより異なります。詳細は各社公式サイトでご確認ください。</p>
      </div>

      <section>
        <h2>ガントチャートツール選びの3つの基準</h2>
        <h3>1. 更新のしやすさ</h3>
        <p>プロジェクトは常に変化します。計画変更時に、手作業でガントチャートを修正する手間が少ないツールを選びましょう。AI対応ツールなら、日報データから自動で進捗を更新できます。</p>
        <h3>2. チームでの共有のしやすさ</h3>
        <p>ガントチャートはプロジェクトマネージャーだけが見るものではありません。メンバー全員が最新の状態を確認でき、担当者ごとに自分のタスクだけを表示できる機能があると理想的です。</p>
        <h3>3. 他機能との連携</h3>
        <p>ガントチャート単体ではなく、タスク管理、日報、ファイル管理、チャットと連携しているツールを選ぶことで、情報の二重管理や転記の手間をなくせます。</p>
      </section>

      <section>
        <h2>無料ツールと有料ツールの使い分け</h2>
        <p>無料のガントチャートツールでも基本的な機能は十分備わっていますが、以下のようなケースでは有料ツールの導入を検討する価値があります。</p>
        <ul>
          <li>プロジェクトの数が3件以上ある</li>
          <li>依存関係やクリティカルパスを厳密に管理したい</li>
          <li>海外拠点や多言語環境で利用する</li>
          <li>日報や会議体と進捗を自動連動させたい</li>
          <li>セキュリティや監査ログが必要</li>
        </ul>
        <p>”おもいむすび”では、無料お試しでAIガントチャートを含む主要機能を試せます（プロジェクト1件・3名まで／期限なし）。まずは実際のプロジェクトで使い勝手を確認してみてください。</p>
      </section>
    </PageShell>
  );
}